"use client"

import UsersIcon from "@/components/icons/UsersIcon"
import { CalendarOutlined, SwapOutlined } from "@ant-design/icons"
import { faCouch, faSearch } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Switch } from "antd"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { flightActions } from "@/lib/redux/features/flight/reduce"
import { cabinClasses } from "@/constants/flight/cabinClass"
import { searchFlightDrawers } from "./DrawerFormSearchFlightWrapper"
import { getQueryStringSearchFlight } from "@/utils/string/getQueryStringSearchFlight"
import { useTERouter } from "@/hooks/app/useTERouter"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

const FormSearchFlightHome = () => {
  const { getAirportCityName, getValidDayjs } = useTETranslation()
  const selectedInputFlightIndex = useAppSelector((state) => state.flight.selectedFligthInputIndex)
  const selectedInputFlight = useAppSelector((state) => state.flight.inputFlights[selectedInputFlightIndex])
  const inputAdult = useAppSelector((state) => state.flight.inputAdult)
  const inputChildren = useAppSelector((state) => state.flight.inputChildren)
  const inputInfant = useAppSelector((state) => state.flight.inputInfant)
  const cabinClass = useAppSelector((state) => state.flight.cabinClass)
  const checkedItinerary = useAppSelector((state) => !!state.flight.checkedItinerary)
  const dispatch = useAppDispatch()
  const router = useTERouter()
  const dateFrom = getValidDayjs(selectedInputFlight?.dateFrom)
  const dateTo = getValidDayjs(selectedInputFlight?.dateTo)
  const { t, i18n } = useTranslation()

  const fromCode = selectedInputFlight?.fromAirport?.code || "—"
  const toCode = selectedInputFlight?.toAirport?.code || "—"
  const fromCity = selectedInputFlight?.fromAirport
    ? getAirportCityName(selectedInputFlight.fromAirport) || selectedInputFlight.fromAirport.country
    : t("flight.from_placeholder")
  const toCity = selectedInputFlight?.toAirport
    ? getAirportCityName(selectedInputFlight.toAirport) || selectedInputFlight.toAirport.country
    : t("flight.to_placeholder")

  const datePattern = i18n.language === "en" ? "ddd, D MMM" : "dd, D [tháng] M"

  const paxCount = (inputAdult || 0) + (inputChildren || 0) + (inputInfant || 0)
  const paxLabel = `${paxCount} ${t("common.guest").toLowerCase()}`
  const cabinLabel = (cabinClasses as any)[cabinClass]?.name
    ? t((cabinClasses as any)[cabinClass].name)
    : t("common.all")

  const handleSearch = () => {
    if (!selectedInputFlight?.toAirport) {
      toast.error(t("flight.airport_to_required"))
      return
    }
    if (!selectedInputFlight?.fromAirport) {
      toast.error(t("flight.airport_from_required"))
      return
    }

    searchFlightDrawers.flights?.open()
    router.push(`/flights/result?${getQueryStringSearchFlight()}`)
  }

  const toggleRoundTrip = (checked: boolean) => {
    dispatch(flightActions.changeCheckedItinerary(checked))
    dispatch(
      flightActions.changeDateTo({
        indexInputFlight: selectedInputFlightIndex,
        dateTo: dateFrom,
      }),
    )
    if (checked) {
      searchFlightDrawers.selectDate?.open("to")
    }
  }

  return (
    <div className="home__searching-flight">
      <div className="home__searching-flight-airports">
        <button
          type="button"
          className="home__searching-flight-route"
          onClick={() => searchFlightDrawers.selectPlacementFrom?.open()}
        >
          <span className="home__searching-flight-route-label">{t("common.from")}</span>
          <span className="home__searching-flight-route-code">{fromCode}</span>
          <span className="home__searching-flight-route-city">{fromCity}</span>
        </button>

        <Button
          shape="circle"
          className="home__searching-flight-swap"
          icon={<SwapOutlined />}
          onClick={() => dispatch(flightActions.swicthBtwAirports({ flightInputIndex: 0 }))}
          aria-label={t("flight.from")}
        />

        <button
          type="button"
          className="home__searching-flight-route home__searching-flight-route--to"
          onClick={() => searchFlightDrawers.selectPlacementTo?.open()}
        >
          <span className="home__searching-flight-route-label">{t("common.to")}</span>
          <span className="home__searching-flight-route-code">{toCode}</span>
          <span className="home__searching-flight-route-city">{toCity}</span>
        </button>
      </div>

      <div className={`home__searching-flight-meta${checkedItinerary ? " is-roundtrip" : ""}`}>
        <button
          type="button"
          className="home__searching-flight-cell"
          onClick={() => searchFlightDrawers.selectDate?.open("from")}
        >
          <span className="home__searching-flight-cell-label">{t("flight.departure_date")}</span>
          <span className="home__searching-flight-cell-value">
            <CalendarOutlined />
            <span>{dateFrom.format(datePattern)}</span>
          </span>
        </button>

        {checkedItinerary ? (
          <button
            type="button"
            className="home__searching-flight-cell"
            onClick={() => searchFlightDrawers.selectDate?.open("to")}
          >
            <span className="home__searching-flight-cell-head">
              <span className="home__searching-flight-cell-label">{t("flight.return_date")}</span>
              <span
                className="home__searching-flight-roundtrip-switch"
                onClick={(e) => e.stopPropagation()}
              >
                <Switch size="small" checked onChange={toggleRoundTrip} />
              </span>
            </span>
            <span className="home__searching-flight-cell-value">
              <CalendarOutlined />
              <span>{dateTo.format(datePattern)}</span>
            </span>
          </button>
        ) : (
          <label className="home__searching-flight-cell home__searching-flight-roundtrip">
            <span className="home__searching-flight-cell-label">{t("flight.round_trip")}</span>
            <Switch size="small" checked={false} onChange={toggleRoundTrip} />
          </label>
        )}

        <button
          type="button"
          className="home__searching-flight-cell"
          onClick={() => searchFlightDrawers.selectPassenger?.open()}
        >
          <span className="home__searching-flight-cell-label">{t("flight.guest_count")}</span>
          <span className="home__searching-flight-cell-value">
            <UsersIcon />
            <span>{paxLabel}</span>
          </span>
        </button>

        <button
          type="button"
          className="home__searching-flight-cell"
          onClick={() => searchFlightDrawers.selectClass?.open()}
        >
          <span className="home__searching-flight-cell-label">{t("flight.class")}</span>
          <span className="home__searching-flight-cell-value">
            <FontAwesomeIcon icon={faCouch} />
            <span>{cabinLabel}</span>
          </span>
        </button>
      </div>

      <Button
        onClick={handleSearch}
        icon={<FontAwesomeIcon icon={faSearch} />}
        size="large"
        type="primary"
        block
        className="home__searching-flight-submit"
      >
        {t("flight.search_flight_short")}
      </Button>
    </div>
  )
}

export default FormSearchFlightHome
